import qs from "qs";
import axios from "../helpers/axios";

import {
  CampTransactionApiResponse,
  CampTransactionMoney,
  CampTransactionsType,
} from "../types/campTransaction";

export type CampTransactionSearchQuery = {
  transactionId?: string;
  userId?: string;
  tradeDateFrom?: string;
  tradeDateTo?: string;
  settlementDateFrom?: string;
  settlementDateTo?: string;
  transactionStatus?: string;
  appCountry?: string;
};

type GetCampTransactionsQuery = {
  page: number;
  pageSize: number;
  searchQuery?: CampTransactionSearchQuery | null;
};

const normalizeMoney = (
  amount: CampTransactionMoney | string | null | undefined,
  fallbackCurrency: string
): CampTransactionMoney => {
  if (amount && typeof amount === "object") return amount;
  return {
    currency: fallbackCurrency,
    quantity: amount ?? "",
  };
};

export const getCampTransactions = async ({
  page,
  pageSize,
  searchQuery,
}: GetCampTransactionsQuery) => {
  const params = {
    page,
    pageSize,
    transactionId: searchQuery?.transactionId,
    userId: searchQuery?.userId,
    tradeDateFrom: searchQuery?.tradeDateFrom,
    tradeDateTo: searchQuery?.tradeDateTo,
    settlementDateFrom: searchQuery?.settlementDateFrom,
    settlementDateTo: searchQuery?.settlementDateTo,
    transactionStatus: searchQuery?.transactionStatus,
    appCountry: searchQuery?.appCountry,
  };
  const { data } = await axios.get<CampTransactionApiResponse>(
    "/admin/camp/transactions",
    {
      params,
      paramsSerializer: (p) => qs.stringify(p, { skipNulls: true }),
    }
  );
  const transactions = data.transactions ?? data.data ?? [];
  const apiTotalCount =
    data.pageInfo?.totalCount ?? data.pageInfo?.total ?? data.total ?? 0;
  const totalCount = searchQuery?.transactionId
    ? transactions.length
    : apiTotalCount;
  const currentPage =
    data.pageInfo?.pageNumber ?? data.pageInfo?.page ?? data.page ?? page;
  const currentPageSize = data.pageInfo?.pageSize ?? data.pageSize ?? pageSize;
  const result: CampTransactionsType = {
    transactions: transactions.map((tx) => ({
      id: tx.id,
      portfolioId: tx.portfolioId,
      securityISIN: tx.securityISIN,
      transactionType: tx.transactionType,
      tradeDate: tx.tradeDate,
      settlementDate: tx.settlementDate,
      valueDate: tx.valueDate ?? undefined,
      valueDateConfirmed: tx.valueDateConfirmed ?? undefined,
      units: tx.unitsConfirmed ?? tx.units,
      grossAmount: normalizeMoney(
        tx.grossAmountConfirmed ?? tx.grossAmount,
        tx.baseCurrency
      ),
      basePrice: normalizeMoney(
        tx.basePriceConfirmed ?? tx.basePrice,
        tx.baseCurrency
      ),
      baseCurrency: tx.baseCurrency,
      transactionStatus: tx.transactionStatus,
      category: tx.category,
      createdAt: tx.createdAt,
      modifiedAt: tx.modifiedAt,
    })),
    pageInfo: {
      totalCount,
      totalPage: Math.ceil(totalCount / currentPageSize),
      pageNumber: currentPage,
      pageSize: currentPageSize,
    },
  };
  return result;
};

export const uploadCampTransactions = async (file: File): Promise<void> => {
  const form = new FormData();
  form.append("file", file);
  await axios.post("/camp-transactions/upload", form);
};
